import { botDefinition } from '../bots/BotCatalog';
import { validateMatchAudioDefinition, type MatchAudioDefinition } from '../audio/MusicDefinition';
import type { ControllerSpec } from '../controllers/ControllerSpec';
import { validateMatchEffectsDefinition, type MatchEffectsDefinition } from '../effects/MatchEffectsDefinition';
import type { PhysicsMatchConfig } from '../physics/PhysicsBackend';
import { validateArenaPresentation, type ArenaPresentation } from '../presentation/ArenaPresentation';
import { validateBallPresentation, type BallPresentation } from '../presentation/BallPresentation';
import { validateTeamPresentation, type TeamPresentation } from '../presentation/TeamPresentation';
import { validateVehiclePresentation, type VehiclePresentation } from '../presentation/VehiclePresentation';
import { exhibitionMatchFlow, freeplayMatchFlow, validateMatchFlowDefinition, type MatchFlowDefinition } from '../rules/MatchFlowDefinition';
import { defaultTrainingDefinition, validateTrainingDefinition, type TrainingDefinition } from '../training/TrainingDefinition';
import type { EntityId, MatchConfig, MatchTeamSize, Team } from '../types';
import { validateGameContentSnapshot, type GameContentSnapshot } from '../content/GameContentContracts';
import { LOCAL_PLAYER_ENTITY_ID } from './MatchRoster';
import { validateResolvedGoalExplosionLoadout, type ResolvedGoalExplosionLoadout } from '../content/GoalExplosionLoadoutContracts';

export interface VehicleDefinition {
  readonly entityId: EntityId;
  readonly displayName: string;
  readonly team: Team;
  readonly controller: ControllerSpec;
  readonly presentation: VehiclePresentation;
}

export interface MatchDefinition {
  readonly starterGoalExplosionLoadout: ResolvedGoalExplosionLoadout;
  readonly authority: 'local' | 'server';
  readonly localEntityId: EntityId | null;
  readonly vehicles: readonly VehicleDefinition[];
  readonly ballPresentation: BallPresentation;
  readonly arenaPresentation: ArenaPresentation;
  readonly teamPresentation: TeamPresentation;
  readonly effects: MatchEffectsDefinition;
  readonly audio: MatchAudioDefinition;
  readonly flow: MatchFlowDefinition;
  readonly physics: PhysicsMatchConfig;
  readonly training: TrainingDefinition | null;
}

export function validateMatchDefinition(definition: MatchDefinition): void {
  if (definition.authority !== 'local' && definition.authority !== 'server') {
    throw new Error(`Unsupported match authority: ${String(definition.authority)}.`);
  }
  if (definition.vehicles.length === 0 && definition.authority === 'local') throw new Error('A match requires at least one vehicle.');
  validateBallPresentation(definition.ballPresentation);
  validateArenaPresentation(definition.arenaPresentation);
  validateTeamPresentation(definition.teamPresentation);
  validateMatchEffectsDefinition(definition.effects);
  validateResolvedGoalExplosionLoadout(definition.starterGoalExplosionLoadout);
  validateMatchAudioDefinition(definition.audio);
  validateMatchFlowDefinition(definition.flow);
  if (definition.training) validateTrainingDefinition(definition.training);
  if (typeof definition.physics.unlimitedBoost !== 'boolean') {
    throw new Error('Match physics unlimitedBoost must be a boolean.');
  }
  const ids = new Set<EntityId>();
  const humanEntityIds: EntityId[] = [];
  for (const vehicle of definition.vehicles) {
    if (!Number.isSafeInteger(vehicle.entityId) || vehicle.entityId <= 0) {
      throw new Error(`Vehicle entity ID must be a positive safe integer; received ${vehicle.entityId}.`);
    }
    if (ids.has(vehicle.entityId)) throw new Error(`Duplicate vehicle entity ID ${vehicle.entityId}.`);
    ids.add(vehicle.entityId);
    if (!vehicle.displayName.trim()) throw new Error(`Vehicle entity ${vehicle.entityId} display name must not be empty.`);
    if (vehicle.controller.kind === 'human') humanEntityIds.push(vehicle.entityId);
    if (vehicle.controller.kind === 'bot') {
      if (!vehicle.controller.botId.trim()) throw new Error(`Vehicle entity ${vehicle.entityId} bot ID must not be empty.`);
      if (definition.authority !== 'server') throw new Error('Bot-controlled vehicles require authoritative server matches.');
    }
    validateVehiclePresentation(vehicle.presentation);
  }
  if (definition.localEntityId === null) {
    if (definition.authority === 'local' && humanEntityIds.length > 0) throw new Error('Spectator matches must not contain a human-controlled vehicle.');
  } else {
    if (!ids.has(definition.localEntityId)) {
      throw new Error(`Local vehicle entity ${definition.localEntityId} is not part of the match roster.`);
    }
    if (!humanEntityIds.includes(definition.localEntityId)) {
      throw new Error('The local entity must be a human-controlled vehicle.');
    }
    if (definition.authority === 'local' && humanEntityIds.length !== 1) {
      throw new Error('The local entity must be the match\'s only human-controlled vehicle.');
    }
  }
}

export function createMatchDefinition(
  config: MatchConfig,
  snapshot: GameContentSnapshot,
  localDisplayName = 'Player',
): MatchDefinition {
  validateGameContentSnapshot(snapshot);
  const teamPresentation = config.mode === 'freeplay'
    ? { colors: { blue: snapshot.freeplayPresentation.color, orange: snapshot.freeplayPresentation.color } }
    : snapshot.teamPresentation;
  const localTeam = config.mode === 'exhibition' ? config.localTeam : 'blue';
  const localVehicle: VehicleDefinition | null = localTeam === null ? null : {
    entityId: LOCAL_PLAYER_ENTITY_ID,
    displayName: localDisplayName.trim() || 'Player',
    team: localTeam,
    controller: { kind: 'human' },
    presentation: config.mode === 'freeplay' ? {
      ...snapshot.vehiclePresentations[localTeam],
      paint: { primary: snapshot.freeplayPresentation.color, secondary: snapshot.freeplayPresentation.color,
        textureColorMode: 'luminance' },
    } : snapshot.vehiclePresentations[localTeam],
  };

  const composition = config.mode === 'exhibition'
    ? createExhibitionVehicles(config, localVehicle, snapshot.vehiclePresentations)
    : { authority: 'local' as const, vehicles: Object.freeze([localVehicle!]) };

  const definition: MatchDefinition = {
    authority: composition.authority,
    localEntityId: localVehicle?.entityId ?? null,
    ballPresentation: snapshot.ballPresentation,
    arenaPresentation: snapshot.arenaPresentation,
    teamPresentation,
    effects: snapshot.effects,
    starterGoalExplosionLoadout: snapshot.starterGoalExplosionLoadout,
    audio: snapshot.audio,
    flow: config.mode === 'exhibition' ? exhibitionMatchFlow() : freeplayMatchFlow(),
    physics: Object.freeze({ unlimitedBoost: config.mode === 'freeplay' }),
    training: config.mode === 'freeplay' ? defaultTrainingDefinition() : null,
    vehicles: composition.vehicles,
  };
  validateMatchDefinition(definition);
  return definition;
}

function createExhibitionVehicles(
  config: Extract<MatchConfig, { mode: 'exhibition' }>,
  localVehicle: VehicleDefinition | null,
  vehiclePresentations: GameContentSnapshot['vehiclePresentations'],
): Readonly<{ authority: 'local' | 'server'; vehicles: readonly VehicleDefinition[] }> {
  validateTeamSize(config.teamSize);
  const botDefinitions = config.bots.map(({ botId, team }) => ({ definition: botDefinition(botId), team }));
  const counts: Record<Team, number> = { blue: 0, orange: 0 };
  if (config.localTeam) counts[config.localTeam] += 1;
  for (const bot of botDefinitions) counts[bot.team] += 1;
  if (counts.blue !== config.teamSize || counts.orange !== config.teamSize) {
    throw new Error(
      `Exhibition ${config.teamSize}v${config.teamSize} requires exactly ${config.teamSize} vehicles on each team.`,
    );
  }

  const vehicles: VehicleDefinition[] = localVehicle ? [localVehicle] : [];
  for (const [index, { definition, team }] of botDefinitions.entries()) {
    const controller: ControllerSpec = { kind: 'bot', botId: definition.id };
    vehicles.push({
      entityId: (localVehicle ? LOCAL_PLAYER_ENTITY_ID + 1 : LOCAL_PLAYER_ENTITY_ID) + index,
      displayName: definition.name,
      team,
      controller,
      presentation: vehiclePresentations[team],
    });
  }
  return Object.freeze({ authority: 'server' as const, vehicles: Object.freeze(vehicles) });
}

function validateTeamSize(teamSize: MatchTeamSize): void {
  if (teamSize !== 1 && teamSize !== 2 && teamSize !== 3) {
    throw new Error(`Unsupported exhibition team size: ${String(teamSize)}.`);
  }
}
